import { useState } from 'react';

import palette from '@lib/core/src/theme/palette';
import AddIcon from '@mui/icons-material/Add';
import ArrowRightSharpIcon from '@mui/icons-material/ArrowRightSharp';
import { Box, Popover, Stack, Typography } from '@mui/material';

import {
  MeetingTemplateDto,
  useListMeetingsTemplates,
  useListPredefinedMeetingsTemplates
} from '@/api/generated';
import { AgentTemplateIconsMap } from '@/enums/AgentTemplateIconsMap';

import { AddTemplateButton } from './AgentTemplateStyles';
import { NoActiveTemplates } from './NoActiveTemplates';
import { TemplateCard } from './TemplateCard';

export const AgentTemplates = ({
  setSelectTemplate,
  isPredefinedTemplateRef
}: {
  setSelectTemplate: React.Dispatch<React.SetStateAction<MeetingTemplateDto | null>>;
  isPredefinedTemplateRef: React.MutableRefObject<boolean>;
}) => {
  const { data: predefinedTemplates } = useListPredefinedMeetingsTemplates();
  const { data: agentTemplates } = useListMeetingsTemplates({
    query: {
      queryKey: ['listMeetingsTemplates']
    }
  });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  const onTemplateCardClick = (isPredefined: boolean, template: MeetingTemplateDto) => {
    // eslint-disable-next-line no-param-reassign
    isPredefinedTemplateRef.current = isPredefined;
    setSelectTemplate(template);
    setAnchorEl(null);
  };
  const customTemplate: MeetingTemplateDto = {
    id: 'custom',
    name: 'Create custom template',
    context: '',
    tags: [],
    sections: [],
    provider: undefined,
    createdAt: undefined,
    updatedAt: undefined
  };

  const predefinedWithCustom = [...(predefinedTemplates?.items || []), customTemplate];

  return (
    <Stack gap={'8px'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: palette.grey[500] }}>
          Active Templates
        </Typography>
        <Box>
          <AddTemplateButton variant="contained" onClick={handleClick}>
            Add Template <ArrowRightSharpIcon sx={{ transform: 'rotate(90deg)' }} />
          </AddTemplateButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            sx={{}}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: '300px'
                }
              }
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
          >
            {predefinedWithCustom.map(template => {
              const isCustom = template.id === customTemplate.id;
              const isPredefined = predefinedTemplates?.items?.some(t => t.id === template.id);
              const description = isCustom
                ? ''
                : isPredefined
                  ? (template as any).description || ''
                  : template.context || '';
              return (
                <TemplateCard
                  key={template.id}
                  icon={
                    isCustom ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          backgroundColor: palette.grey[100],
                          borderRadius: 1
                        }}
                      >
                        <AddIcon sx={{ color: palette.grey[500] }} />
                      </Box>
                    ) : (
                      AgentTemplateIconsMap[template.name || '']
                    )
                  }
                  containerStyle={{ border: '0 none' }}
                  name={template.name || ''}
                  description={description}
                  showTags={false}
                  onClick={() => onTemplateCardClick(true, template)}
                />
              );
            })}
          </Popover>
        </Box>
      </Box>
      <Stack gap={'6px'}>
        {agentTemplates?.items?.length ? (
          agentTemplates.items.map(template => (
            <TemplateCard
              key={template.id}
              icon={AgentTemplateIconsMap[template.name || '']}
              name={template.name || ''}
              description={template.context || ''}
              tags={template.tags || []}
              onClick={() => onTemplateCardClick(false, template)}
            />
          ))
        ) : (
          <NoActiveTemplates />
        )}
      </Stack>
    </Stack>
  );
};
